/**
 * V210.5: Subscription Protection Hook
 *
 * Purpose: Verify user's subscription status on app load and repair
 * entitlement ↔ users row drift (Google + Apple).
 *
 * Usage:
 * ```tsx
 * import { useSubscriptionProtection } from '@/features/subscription/hooks/useSubscriptionProtection';
 *
 * function App() {
 *   const { user, userProfile } = useAuth();
 *   const { entitlements } = useEntitlements(user?.id || null);
 *   useSubscriptionProtection(user, userProfile, entitlements);
 *   // ...
 * }
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import type { Entitlements } from '@/services/entitlement/EntitlementService';

interface SubscriptionCheckResult {
  valid: boolean;
  reason?: string;
  message?: string;
  expiryDate?: string;
  error?: string;
}

interface UserProfile {
  id: string;
  is_verified?: boolean;
  verified_at?: string | null;
  subscription_expires_at?: string;
  [key: string]: any;
}

/**
 * Hook to protect against subscription state drift and repair entitlement-only verified users.
 */
export function useSubscriptionProtection(
  user: User | null | undefined,
  userProfile: UserProfile | null | undefined,
  entitlements?: Entitlements | null | undefined,
) {
  const hasCheckedRef = useRef(false);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current || isCheckingRef.current) {
      return;
    }

    if (!user?.id || !userProfile?.id) {
      return;
    }

    const hasVerificationMarker =
      userProfile.is_verified === true ||
      !!userProfile.verified_at;

    const needsEntitlementRepair =
      entitlements?.isVerified === true &&
      userProfile.is_verified !== true;

    if (!hasVerificationMarker && !needsEntitlementRepair) {
      console.log('🛡️ V210.5: No verification drift detected — skipping protection check');
      hasCheckedRef.current = true;
      return;
    }

    console.log('🛡️ V210.5: Running subscription protection / entitlement repair...', {
      userId: user.id,
      isVerified: userProfile.is_verified,
      entitlementsVerified: entitlements?.isVerified,
      expiresAt: userProfile.subscription_expires_at,
    });

    isCheckingRef.current = true;
    checkSubscriptionStatus(user.id)
      .finally(() => {
        hasCheckedRef.current = true;
        isCheckingRef.current = false;
      });

  }, [
    user?.id,
    userProfile?.id,
    userProfile?.is_verified,
    userProfile?.verified_at,
    entitlements?.isVerified,
  ]);
}

/**
 * Repair users.is_verified drift when entitlements confirm active subscription.
 * Invokes google-check-status (handles Google verify + Apple DB repair).
 */
export async function repairEntitlementDrift(userId: string): Promise<boolean> {
  if (!userId) return false;

  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error('❌ V210.5: No active session for entitlement repair');
      return false;
    }

    const { data, error } = await supabase.functions.invoke('google-check-status', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) {
      console.error('❌ V210.5: Entitlement repair failed:', error);
      return false;
    }

    console.log('✅ V210.5: Entitlement repair result:', data);
    return data?.valid === true;
  } catch (error) {
    console.error('❌ V210.5: Unexpected entitlement repair error:', error);
    return false;
  }
}

/**
 * Check subscription status with Google API (and Apple DB repair when applicable)
 */
async function checkSubscriptionStatus(userId: string): Promise<void> {
  try {
    console.log('🛡️ V210.5: Starting subscription protection check for user:', userId);

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ V210.5: No active session, skipping check');
      return;
    }

    const { data, error } = await supabase.functions.invoke(
      'google-check-status',
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );

    if (error) {
      console.error('❌ V210.5: Edge function error:', error);
      return;
    }

    console.log('✅ V210.5: Protection check result:', data);

    if (data && !data.valid) {
      console.warn('⚠️ V210.5: Subscription mismatch detected!', {
        reason: data.reason,
        message: data.message,
      });

      await new Promise(resolve => setTimeout(resolve, 1000));

      const { error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('❌ V210.5: Failed to refresh session:', refreshError);
      } else {
        console.log('✅ V210.5: Session refreshed, user should see updated verification status');
        
        if (typeof window !== 'undefined') {
          console.log('🔄 V210.5: Reloading page to apply verification changes...');
          window.location.reload();
        }
      }
    } else if (data?.message?.includes('synced')) {
      console.log('✅ V210.5: Entitlement drift repaired — refreshing session');
      await new Promise(resolve => setTimeout(resolve, 500));
      await supabase.auth.refreshSession();
    } else {
      console.log('✅ V210.5: Subscription is valid, no action needed');
    }

  } catch (error) {
    console.error('❌ V210.5: Unexpected error in subscription protection:', error);
  }
}

/**
 * Manual subscription check (for testing or manual triggers)
 */
export async function manualSubscriptionCheck(): Promise<SubscriptionCheckResult> {
  try {
    console.log('🛡️ V210.5: Manual subscription check triggered');

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      return {
        valid: false,
        error: 'No active session',
      };
    }

    const { data, error } = await supabase.functions.invoke(
      'google-check-status',
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );

    if (error) {
      return {
        valid: false,
        error: error.message,
      };
    }

    return data || { valid: false, error: 'No response from server' };

  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Hook variant that returns the check result (for UI display)
 */
export function useSubscriptionProtectionWithResult(
  user: User | null | undefined,
  userProfile: UserProfile | null | undefined
) {
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<SubscriptionCheckResult | null>(null);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current || !user?.id || !userProfile?.is_verified) {
      return;
    }

    hasCheckedRef.current = true;
    setIsChecking(true);

    manualSubscriptionCheck()
      .then(setResult)
      .finally(() => setIsChecking(false));

  }, [user?.id, userProfile?.is_verified]);

  return { isChecking, result };
}
